import {
  convert_kilometres_to_meters,
  convert_kilometres_to_miles,
  convert_kilometres_to_yards,
} from './unitUtils'

export type Coordinate = {
  latitude: number
  longitude: number
}

type SupportedUnitOptions = 'kilometres' | 'meters' | 'miles' | 'yards'

export const haversineDistanceLessThanTwoCoordsErrorMsg = 'You must provide at two coordinates to measure the distance between them.'
export const haversineDistanceMoreThanTwoCoordsErrorMsg = 'Please only provide two coordinates so we know which ones you want to measure the distance between.'
export const haversineDistanceUnsupportedUnitErrorMsg = 'Unit request is not supported. Unit must be one of kilometres, meters, miles or yards'

/**
 * Calculates the straight line distance between two lon/lat coordinates.
 *
 * @param coords a list of exactly two coordinates in the form of Coordinate objects.
 * @param unit the unit you want the return to be in. Supported units are kilometres, meters, miles, yards. Defaults to meters.
 * @returns distance between the two coordinates.
 */
export default function haversineDistance({
  coords,
  unit = 'meters',
}: {
  coords: [Coordinate, Coordinate]
  unit?: SupportedUnitOptions
}): number {
  // check two coordinates have been provided
  if (coords.length < 2) throw new Error(haversineDistanceLessThanTwoCoordsErrorMsg)
  if (coords.length > 2) throw new Error(haversineDistanceMoreThanTwoCoordsErrorMsg)
  // check unit requested is supported
  if (unit && unit !== 'kilometres' && unit !== 'meters' && unit !== 'miles' && unit !== 'yards') throw new Error(haversineDistanceUnsupportedUnitErrorMsg) 

  // convert point to radian helper function
  function toRad(x: number): number {
    return (x * Math.PI) / 180
  }

  // work out distance in km via haversine distance calculation
  const lon1 = coords[0].longitude
  const lat1 = coords[0].latitude

  const lon2 = coords[1].longitude
  const lat2 = coords[1].latitude

  const R = 6371 // km

  const x1 = lat2 - lat1
  const dLat = toRad(x1)
  const x2 = lon2 - lon1
  const dLon = toRad(x2)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance_km = R * c

  // return as selected unit
  if (unit === 'kilometres') return distance_km
  if (unit === 'meters') return convert_kilometres_to_meters(distance_km)
  if (unit === 'miles') return convert_kilometres_to_miles(distance_km)
  if (unit === 'yards') return convert_kilometres_to_yards(distance_km)

  // default to meters if something went wrong
  return convert_kilometres_to_meters(distance_km)
}
